import {
  ApolloCache,
  DefaultContext,
  FetchResult,
  LazyQueryExecFunction,
  MutationFunctionOptions,
} from '@apollo/client';
import {ReadFieldFunction} from '@apollo/client/cache/core/types/common';
import {Typography} from '@mui/material';
import {GridRowParams} from '@mui/x-data-grid-pro';
import {OptionsObject, SnackbarKey, SnackbarMessage} from 'notistack';
import React from 'react';
import {NavigateFunction} from 'react-router-dom';
import {
  DeleteOvbuilder,
  DeleteOvbuilderVariables,
} from '../../../schema-types/DeleteOvbuilder';
import {
  OvbuilderAssociatedItems,
  OvbuilderAssociatedItemsVariables,
} from '../../../schema-types/OvbuilderAssociatedItems';
import {OVBuilders_ovrecommends} from '../../../schema-types/OVBuilders';
import {SaltusAny} from '../../../utils/SaltusAny';
import saltus from '../../../shared/logger';
import {builderGridStyles} from './builderStyles';
import validateUUID from '../../../utils/validateUUID';

export const onCompletedDeleteBuilderFactory = (
  enqueueSnackbar: (
    message: SnackbarMessage,
    options?: OptionsObject | undefined
  ) => SnackbarKey
) => {
  return (deletionData: DeleteOvbuilder) => {
    const {deleteOvrecommend: deleteRecommendationData} = deletionData;
    if (!deleteRecommendationData?.ok) {
      enqueueSnackbar('Unable to delete the recommendation.', {
        variant: 'error',
        autoHideDuration: null,
      });
      return;
    }
    enqueueSnackbar('The selected review has been deleted.', {
      variant: 'success',
    });
  };
};

export const deleteSelectedBuilderFactory = ({
  recommends,
  enqueueSnackbar,
  getAssociatedReccs,
  closeSnackbar,
  accountId,
  reviewId,
  deleteRecommendation,
}: {
  accountId?: string;
  reviewId?: string;
  recommends: OVBuilders_ovrecommends[];
  enqueueSnackbar: (
    message: SnackbarMessage,
    options?: OptionsObject | undefined
  ) => SnackbarKey;
  getAssociatedReccs: LazyQueryExecFunction<
    OvbuilderAssociatedItems,
    OvbuilderAssociatedItemsVariables
  >;
  closeSnackbar: (key?: SnackbarKey | undefined) => void;
  deleteRecommendation: (
    options?:
      | MutationFunctionOptions<
          DeleteOvbuilder,
          DeleteOvbuilderVariables,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined
  ) => Promise<any>;
}) => {
  const classes = builderGridStyles();
  return (ovrecommendId: string) => {
    const recc = recommends.find((x) => x.id === ovrecommendId);

    // if (!recc) {
    //   enqueueSnackbar('Unable to delete recc');
    //   return;
    // }
    enqueueSnackbar(`Attempting to delete recommendation "${recc?.name}"`, {
      persist: true,
    });
    // eslint-disable-next-line consistent-return
    return getAssociatedReccs({
      variables: {
        ovrecommendId: validateUUID(ovrecommendId)!,
      },
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
    }).then((result) => {
      const {data: assocRecsData} = result;
      if (!assocRecsData?.ovrecommendDetails?.pots) {
        throw Error('This recommendation contains no pots');
      }
      const reccs: {name: string; id: string}[] = [];

      for (const pot of assocRecsData.ovrecommendDetails.pots) {
        // check if its the original recc that created the pot, if so prevent delete
        const isDummyAndCreatedByRecc =
          pot?.pot.isDummy && pot.pot.originalOvrId === ovrecommendId;
        const isShellPlanAndCreatedByRecc =
          (pot?.pot?.servicingRights === 'LOA required' ||
            pot?.pot?.servicingRights === 'No information rights') &&
          pot.pot.originalOvrId === ovrecommendId;

        if (isShellPlanAndCreatedByRecc || isDummyAndCreatedByRecc) {
          const dependentReccs = pot.pot.ovrecommendpots;
          if (dependentReccs.length > 1) {
            for (const assocRec of pot.pot.ovrecommendpots) {
              // When checking for ability to delete a recc, ignore associated reccs that have isDeleted=true
              if (assocRec.ovrecommend && !assocRec.ovrecommend.isDeleted) {
                if (assocRec.ovrecommend.id !== ovrecommendId) {
                  reccs.push({
                    name: assocRec.ovrecommend.name || 'Unnamed',
                    id: assocRec.ovrecommend.id,
                  });
                }
              }
            }
          }
        }
      }
      if (reccs.length) {
        // remove loading
        closeSnackbar();
        enqueueSnackbar(
          <div>
            <Typography>
              Could not delete {recc?.name ? `"${recc.name}"` : 'recommendation'} as it
              contains shell plans that are used by other recommendations. <br />
              Delete these first before deleting this recommendation:
              <br />
              {reccs.map((assocRec) => {
                return (
                  <div key={assocRec.id}>
                    <a
                      style={classes.text}
                      target="_blank"
                      href={`/account/${accountId}/review-journey/${reviewId}/builder?ovrecommendId=${assocRec.id}`}
                      rel="noreferrer"
                    >
                      {assocRec.name}
                    </a>
                  </div>
                );
              })}
            </Typography>
          </div>,
          {variant: 'error'}
        );
        return;
      }

      // remove loading
      closeSnackbar();

      // eslint-disable-next-line consistent-return
      return deleteRecommendation({
        variables: {
          ovrecommendId,
        },
      });
    });
  };
};

export const updateEditorGridFactory = (navigate: NavigateFunction) => {
  return (ovrecommendId: string) => {
    navigate({
      pathname: `../builder`,
      search: `ovrecommendId=${ovrecommendId}`,
    });
  };
};

export const onRowDoubleClickFactory = (navigate: NavigateFunction) => {
  return (param: GridRowParams) => {
    const {id} = param;
    navigate({pathname: `../builder`, search: `ovrecommendId=${id}`});
  };
};

export const updateDeleteRecommendation = (
  cache: ApolloCache<any>,
  fetchResult: Omit<
    FetchResult<DeleteOvbuilder, Record<string, any>, Record<string, any>>,
    'context'
  >
) => {
  const {data: updatedData, errors} = fetchResult;
  const deleteOvrecommend = updatedData?.deleteOvrecommend;
  if (!deleteOvrecommend?.ok || errors) {
    saltus.log(errors?.map((x) => x.message).join(','));
    return;
  }

  cache.modify({
    fields: {
      ovrecommends(
        existingRecommendations: SaltusAny,
        {readField}: {readField: ReadFieldFunction}
      ) {
        return existingRecommendations.filter(
          (n: SaltusAny) => readField('id', n) !== deleteOvrecommend.ovrecommendId
        );
      },
    },
  });
};
